
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { User, UserFromDB } from '@/types/user';
import { convertToUser } from '@/utils/userUtils';
import { useUserGroups } from './useUserGroups';

export const useUsers = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { getUserGroups, updateUserGroups } = useUserGroups();

  const {
    data: users = [],
    isLoading: loading,
    error
  } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('users')
        .select(`
          *,
          clients:client_id (
            id,
            name
          )
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching users:', error);
        
        // Se o erro for 406 ou relacionado a RLS, vamos tentar uma consulta mais simples
        if (error.code === 'PGRST301' || error.message.includes('406')) {
          const { data: simpleData, error: simpleError } = await supabase
            .from('users')
            .select('*')
            .order('created_at', { ascending: false });
          
          if (simpleError) {
            throw simpleError;
          }
          
          const convertedUsers = (simpleData as UserFromDB[]).map(user => ({
            ...convertToUser(user),
            client: null // Sem dados do cliente por enquanto
          }));
          return convertedUsers;
        }
        
        throw error;
      }

      const convertedUsers = await Promise.all((data as UserFromDB[]).map(async (user) => {
        const convertedUser = convertToUser(user);
        
        // Se o usuário tem client_ids (múltiplos clientes), buscar os dados de cada cliente
        if (user.client_ids && Array.isArray(user.client_ids) && user.client_ids.length > 0) {
          const { data: clientsData } = await supabase
            .from('clients')
            .select('id, name')
            .in('id', user.client_ids);
          
          if (clientsData) {
            convertedUser.clients = clientsData;
          }
        }
        
        return convertedUser;
      }));
      
      return convertedUsers;
    },
  });

  const addUser = useMutation({
    mutationFn: async (userData: any) => {
      const { data, error } = await supabase
        .from('users')
        .insert([userData])
        .select(`
          *,
          clients:client_id (
            id,
            name
          )
        `)
        .single();

      if (error) {
        console.error('Error adding user:', error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({
        title: "Sucesso",
        description: "Usuário criado com sucesso!",
      });
    },
    onError: (error) => {
      console.error('Error adding user:', error);
      toast({
        title: "Erro",
        description: "Erro ao criar usuário. Tente novamente.",
        variant: "destructive",
      });
    },
  });

  const updateUser = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const { groups, client_ids, ...userData } = data;
      
      // Clean client_ids to valid UUIDs
      let cleanClientIds: string[] | undefined = undefined;
      if (client_ids !== undefined) {
        cleanClientIds = [];
        if (Array.isArray(client_ids)) {
          cleanClientIds = client_ids
            .map((cid: any) => String(cid).trim())
            .filter((cid: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(cid));
        }
      }

      

      // Single atomic RPC call for all user data + client_ids
      const { data: result, error: rpcError } = await supabase.rpc('update_user_complete', {
        p_user_id: id,
        p_name: userData.name ?? null,
        p_email: userData.email ?? null,
        p_phone: userData.phone ?? null,
        p_department: userData.department ?? null,
        p_role: userData.role ?? null,
        p_client_id: userData.client_id ?? null,
        p_client_ids: cleanClientIds ?? null,
        p_status: userData.status ?? null,
      });

      if (rpcError) {
        console.error('Error in update_user_complete RPC:', rpcError);
        throw rpcError;
      }

      

      // Verify persistence
      const { data: verification } = await supabase
        .from('users')
        .select('id, client_ids')
        .eq('id', id)
        .single();
      
      

      // Update groups if provided
      if (groups !== undefined) {
        await updateUserGroups(id, groups);
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({
        title: "Sucesso",
        description: "Usuário atualizado com sucesso!",
      });
    },
    onError: (error) => {
      console.error('Error updating user:', error);
      const msg = error.message || '';
      if (msg.includes('licença') || msg.includes('limite') || msg.includes('license')) {
        toast({
          title: "Erro de licença",
          description: msg || "O cliente selecionado não possui licença ativa ou o limite de usuários foi atingido.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Erro",
          description: "Erro ao atualizar usuário. Tente novamente.",
          variant: "destructive",
        });
      }
    },
  });

  const deleteUser = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('users')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting user:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({
        title: "Sucesso",
        description: "Usuário removido com sucesso!",
      });
    },
    onError: (error) => {
      console.error('Error deleting user:', error);
      toast({
        title: "Erro",
        description: "Erro ao remover usuário. Tente novamente.",
        variant: "destructive",
      });
    },
  });

  return {
    users,
    loading,
    error,
    addUser,
    updateUser,
    deleteUser,
    getUserGroups,
  };
};
